import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  U3500DeliverableChecklistItem,
  U3500DeliverableChecklistResponse,
  U3500DeliverableKind,
  U3500DeliverableStatus,
} from '../../../../../u3500GedDocumentSearch/U3500Service';
import { NS } from '../../../../U3121';

export interface U3121ProjectDeliverableChecklistProps {
  checklist: U3500DeliverableChecklistResponse | null;
  loading: boolean;
  onOpenDocument: (documentId: string, fileName?: string | null) => void;
  onRefresh: () => void;
}

function kindLabelKey(kind: U3500DeliverableKind): string {
  switch (kind) {
    case 'CL':
      return 'lblDeliverableKindCL';
    case 'DT':
      return 'lblDeliverableKindDT';
    case 'PIQ':
      return 'lblDeliverableKindPIQ';
    case 'OCTR':
      return 'lblDeliverableKindOCTR';
    default:
      return 'lblDeliverableKindCL';
  }
}

function statusBadgeClass(status: U3500DeliverableStatus): string {
  if (status === 'present') {
    return 'bg-emerald-100 text-emerald-900 border-emerald-300';
  }
  return 'bg-rose-100 text-rose-900 border-rose-300';
}

const U3121ProjectDeliverableChecklist: React.FC<U3121ProjectDeliverableChecklistProps> = ({
  checklist,
  loading,
  onOpenDocument,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const items = checklist?.items ?? [];
  const loaded = checklist !== null;

  const handleItemClick = (item: U3500DeliverableChecklistItem) => {
    if (item.status !== 'present' || !item.matchedDocumentId) {
      return;
    }
    onOpenDocument(item.matchedDocumentId, item.matchedFileName);
  };

  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-800">
          {t('lblDeliverableChecklist', { ns: NS })}
          {checklist?.projetNumber ? (
            <span className="ml-2 font-normal text-gray-500">({checklist.projetNumber})</span>
          ) : null}
        </div>
        <button
          type="button"
          className="shrink-0 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
          disabled={loading}
          onClick={onRefresh}
        >
          {t('btnRefreshDeliverableChecklist', { ns: NS })}
        </button>
      </div>

      {loading && (
        <p className="text-sm text-gray-500">{t('msgDeliverableChecklistLoading', { ns: NS })}</p>
      )}

      {!loading && loaded && items.length === 0 && (
        <p className="text-sm text-gray-500">{t('msgDeliverableChecklistEmpty', { ns: NS })}</p>
      )}

      {!loading && items.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {items.map((item) => {
            const isPresent = item.status === 'present';
            const clickable = isPresent && Boolean(item.matchedDocumentId);
            const statusLabel = isPresent
              ? t('lblDeliverableStatusPresent', { ns: NS })
              : t('lblDeliverableStatusMissing', { ns: NS });
            const titleParts = [
              t(kindLabelKey(item.kind), { ns: NS }),
              statusLabel,
              item.matchedFileName || item.matchedRelativePath || null,
            ].filter(Boolean);

            return (
              <li key={item.kind}>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium ${statusBadgeClass(item.status)} ${
                    clickable
                      ? 'cursor-pointer hover:opacity-90'
                      : 'cursor-default'
                  }`}
                  disabled={!clickable}
                  title={titleParts.join(' — ')}
                  onClick={() => handleItemClick(item)}
                >
                  <span>{t(kindLabelKey(item.kind), { ns: NS })}</span>
                  <span className="font-normal opacity-80">{statusLabel}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-2 text-xs text-gray-500">{t('msgDeliverableChecklistIntrantsHint', { ns: NS })}</p>
    </div>
  );
};

export default U3121ProjectDeliverableChecklist;
